import React from 'react';
import { SelectChangeEvent, OptionTypeBase, Select, Text } from '@cad-arc';
import { adaptSelectOnChange } from '../helpers/adapt-select-on-change';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { FieldPath, FieldValues } from 'react-hook-form';
import { OptionOrOptions, SelectOptions } from '../option';
import { useFocusManager } from '@react-aria/focus';
import { genericMemo } from '../../common/helpers/memo';
import { moveFocusNext } from '../helpers/move-focus';
import { BaseCreatableSelect } from '../inputs/base-creatable-select';

type SelectProps = React.ComponentProps<typeof Select>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    options: SelectOptions;
    isMultiple?: boolean;
    selectProps?: Partial<SelectProps>;
    isClearable?: SelectProps['isClearable'];
    disableSetFullOptionAsValue?: boolean;
    truncateSelectedCount?: SelectProps['truncateSelectedCount'];
    sortOptions?: SelectProps['sortOptions'];
    menuPlacement?: SelectProps['menuPlacement'];
};

export const FormCreatableSelect = genericMemo(function FormSelect<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const {
        selectProps,
        options,
        isMultiple,
        isClearable,
        truncateSelectedCount,
        renderViewMode,
        sortOptions,
        menuPlacement,
        ...integratedFormControlProps
    } = props;
    const selectPropsMemo = React.useMemo(() => selectProps ?? {}, [selectProps]);
    const [finalOptions, setFinalOptions] = React.useState(options);

    const renderViewModeToUse = React.useCallback(
        (value: OptionOrOptions) => {
            if (renderViewMode) {
                return renderViewMode(value);
            }
            if (Array.isArray(value)) {
                return (
                    <Text fontSize="12px">{value.map((option) => option.label).join(', ')}</Text>
                );
            }
            return <Text>{value.label}</Text>;
        },
        [renderViewMode]
    );

    const focusManager = useFocusManager();

    return (
        <IntegratedFormControl<OptionOrOptions>
            {...integratedFormControlProps}
            renderViewMode={renderViewModeToUse}
        >
            {(inputProps) => {
                function onChange(value: SelectChangeEvent<OptionTypeBase, boolean>) {
                    if (props.disableSetFullOptionAsValue === true) {
                        inputProps.onChange(value);
                        moveFocusNext(focusManager, value.currentTarget.value, isMultiple);
                    } else {
                        adaptSelectOnChange(inputProps.onChange, finalOptions)(value);
                        moveFocusNext(focusManager, value.currentTarget.value, isMultiple);
                    }
                }
                return (
                    // @ts-expect-error erroneous error around ref type
                    <BaseCreatableSelect
                        {...inputProps}
                        menuPlacement={menuPlacement}
                        isMultiple={isMultiple}
                        isClearable={isClearable}
                        options={finalOptions}
                        onCreateOption={(input: string) => {
                            setFinalOptions((prev) => [...prev, { label: input, value: input }]);
                            // @ts-expect-error event shape
                            onChange({ target: { value: input }, currentTarget: { value: input } });
                        }}
                        truncateSelectedCount={truncateSelectedCount}
                        sortOptions={sortOptions}
                        onChange={onChange}
                        {...selectPropsMemo}
                    />
                );
            }}
        </IntegratedFormControl>
    );
});
